import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useSubscriptions } from '@/hooks/useAzureResources';
import {
  getAppInsightsResources,
  type AppInsightsResource,
} from '@/services/applicationInsightsService';
import { isArmMockMode } from '@/services/armConfig';
import type { AzureSubscription } from '@/services/azureResourceService';

/** Picker-friendly shape: subscriptions grouped with their AI resources. */
export interface SubscriptionGroup {
  subscription: AzureSubscription;
  resources: AppInsightsResource[];
}

interface UseAppInsightsResourcesResult {
  /** True while either the subscription list or the AI list is in flight. */
  isLoading: boolean;
  /** Either underlying query failed. */
  isError: boolean;
  /** Subscriptions that contain at least one AI resource, sorted by display name. */
  groups: SubscriptionGroup[];
  /** Total resources across all subscriptions (zero → "no resources anywhere" empty state). */
  totalResources: number;
  /** Re-run both queries (used by the Retry button). */
  refetch: () => Promise<unknown>;
}

const STALE_TIME = 1000 * 60 * 60 * 8;

/**
 * Composite hook that powers the Application Insights resource picker.
 *
 * Issues a single Resource Graph query across every subscription the user can
 * read, then groups the results client-side. Subscriptions with zero AI
 * resources are filtered out so the dropdown only contains pickable options
 * (per the Observability UX decision).
 */
export const useApplicationInsightsResources = (enabled = true): UseAppInsightsResourcesResult => {
  const { getArmToken, isAuthenticated } = useAuth();

  const subsQuery = useSubscriptions(enabled && (isAuthenticated || isArmMockMode()));

  const resourcesQuery: UseQueryResult<AppInsightsResource[], Error> = useQuery({
    queryKey: ['appInsightsResources', subsQuery.data?.map((s) => s.subscriptionId).sort()],
    queryFn: async () => {
      // Zero subscriptions → nothing to query for. Skip the token round-trip
      // and return an empty list directly so the picker shows its "no
      // resources anywhere" empty state immediately.
      const subs = subsQuery.data ?? [];
      if (subs.length === 0) return [];
      if (isArmMockMode()) {
        return getAppInsightsResources('', subs);
      }
      const token = await getArmToken();
      if (!token) throw new Error('Failed to acquire ARM token');
      return getAppInsightsResources(token, subs);
    },
    enabled: enabled && !!subsQuery.data,
    staleTime: STALE_TIME,
    gcTime: STALE_TIME,
    retry: 1,
  });

  const groups = useMemo<SubscriptionGroup[]>(() => {
    if (!subsQuery.data || !resourcesQuery.data) return [];
    const bySub = new Map<string, AppInsightsResource[]>();
    for (const r of resourcesQuery.data) {
      const existing = bySub.get(r.subscriptionId);
      if (existing) existing.push(r);
      else bySub.set(r.subscriptionId, [r]);
    }
    return subsQuery.data
      .filter((sub) => bySub.has(sub.subscriptionId))
      .map((sub) => ({
        subscription: sub,
        resources: (bySub.get(sub.subscriptionId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.subscription.displayName.localeCompare(b.subscription.displayName));
  }, [subsQuery.data, resourcesQuery.data]);

  return {
    isLoading: subsQuery.isLoading || resourcesQuery.isLoading,
    isError: subsQuery.isError || resourcesQuery.isError,
    groups,
    totalResources: resourcesQuery.data?.length ?? 0,
    refetch: async () => {
      await subsQuery.refetch();
      await resourcesQuery.refetch();
    },
  };
};
